import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { FilledButton } from '@/components/core/Button/variants/FilledButton';
import { ErrorLayout } from '@/components/ui/shared/layouts/ErrorLayout/ErrorLayout';
import Bugsnag from '@/lib/bugsnag';
import { ButtonVariant } from '@/styles/themes/button';

interface ICustomErrorProps {
  shouldSkipLoginRedirect?: boolean;
  statusCode: number;
}

const Error: NextPage<ICustomErrorProps> = ({ statusCode, shouldSkipLoginRedirect }) => {
  const router = useRouter();
  const { t } = useTranslation();

  useEffect(() => {
    if (statusCode === 401 && !shouldSkipLoginRedirect) {
      router.push(`/log-in?redirectUrl=${encodeURIComponent(router.asPath)}`);
    }
  }, [statusCode, shouldSkipLoginRedirect, router]);

  const is404 = statusCode === 404;
  const errorCode = is404 ? '404' : '500';
  const message = t(is404 ? 'notFoundPage.message' : 'errorPage.message');
  const buttonLabel = t(is404 ? 'notFoundPage.buttonLabel' : 'errorPage.buttonLabel');

  const handleRefresh = () => {
    if (is404) {
      router.push(`/`);
    } else {
      window?.location.reload();
    }
  };

  // if the status in not logged in it will navigate to log in page
  if (statusCode === 401 && !shouldSkipLoginRedirect) {
    return null;
  }

  return (
    <ErrorLayout errorCode={errorCode} message={message}>
      <FilledButton variant={ButtonVariant.ACTION_FILLED} onClick={handleRefresh}>
        {buttonLabel}
      </FilledButton>
    </ErrorLayout>
  );
};

Error.getInitialProps = ({ res, err }) => {
  if (err) {
    Bugsnag.notify(err);
  }

  const statusCode = res?.statusCode || err?.statusCode || 400;

  return { statusCode };
};

export default Error;
